import React from "react";

export type ModalBodyProps = {
  children: React.ReactNode;
};

/** Wrapper for AntD Modal contents that applies the Insilico tokens.
 *  Use this for any persona-flow modal so font, spacing, radii are consistent. */
export const ModalBody: React.FC<ModalBodyProps> = ({ children }) => (
  <div className="insilico-scope scale-baseline-80 flex flex-col gap-4 pt-2" style={{ fontFamily: "var(--insilico-font)" }}>
    {children}
  </div>
);

export type InsilicoInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: React.ReactNode;
  label?: string;
};
export const InsilicoInput: React.FC<InsilicoInputProps> = ({
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  className,
  error,
  id,
  label,
  style,
  ...props
}) => {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(" ");

  return (
    <div>
      {label && (
        <label
          className="block mb-1"
          htmlFor={inputId}
          style={{ fontSize: 12, fontWeight: 600, color: "var(--insilico-grey)", textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          {label}
        </label>
      )}
      <input
        {...props}
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : ariaInvalid}
        className={`w-full rounded-[var(--insilico-input-radius)] px-4 py-2 outline-none ${className ?? ""}`}
        id={inputId}
        style={{
          ...style,
          border: error ? "1px solid var(--insilico-danger)" : "1px solid var(--insilico-line)",
          background: "var(--insilico-pill-tint)",
          fontSize: 14,
          color: "var(--insilico-blue-dark)",
          fontFamily: "var(--insilico-font)",
        }}
      />
      {error && (
        <p
          className="mt-1 text-xs"
          id={errorId}
          style={{ color: "var(--insilico-danger)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export type InsilicoTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: React.ReactNode;
  label?: string;
};
export const InsilicoTextarea: React.FC<InsilicoTextareaProps> = ({
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  className,
  error,
  id,
  label,
  style,
  ...props
}) => {
  const generatedId = React.useId();
  const textareaId = id ?? generatedId;
  const errorId = error ? `${textareaId}-error` : undefined;
  const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(" ");

  return (
    <div>
      {label && (
        <label
          className="block mb-1"
          htmlFor={textareaId}
          style={{ fontSize: 12, fontWeight: 600, color: "var(--insilico-grey)", textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          {label}
        </label>
      )}
      <textarea
        {...props}
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : ariaInvalid}
        className={`w-full rounded-[var(--insilico-input-radius)] px-4 py-2 outline-none resize-none ${className ?? ""}`}
        id={textareaId}
        style={{
          ...style,
          border: error ? "1px solid var(--insilico-danger)" : "1px solid var(--insilico-line)",
          background: "var(--insilico-pill-tint)",
          fontSize: 14,
          color: "var(--insilico-blue-dark)",
          fontFamily: "var(--insilico-font)",
        }}
      />
      {error && (
        <p
          className="mt-1 text-xs"
          id={errorId}
          style={{ color: "var(--insilico-danger)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
};
